<template>
  <v-container fluid class="login-container fill-height pa-0">
    <v-row no-gutters class="fill-height">
      <!-- Lado Esquerdo - Informações (Desktop apenas) -->
      <v-col cols="12" md="7" class="info-section d-none d-md-flex">
        <div class="info-content">
          <v-img
              src="../../assets/icons/Gestor_Logo_.svg"
              max-width="280"
              contain
              class="mb-10"
          ></v-img>

          <!-- Features -->
          <div class="features-list">
            <div
                class="feature-item mb-4"
                v-for="(feature, index) in features"
                :key="feature.icon"
            >
              <transition name="feature-fade" appear>
                <div
                    v-if="featuresVisiveis.includes(feature.icon)"
                    class="feature-content"
                    :style="{ transitionDelay: `${index * 150}ms` }"
                >
                  <v-icon color="white" size="24" class="mr-3">{{ feature.icon }}</v-icon>
                  <div>
                    <div class="white--text font-weight-medium">{{ feature.title }}</div>
                    <div class="white--text text--lighten-1 caption">{{ feature.subtitle }}</div>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </v-col>

      <!-- Lado Direito - Formulário -->
      <v-col cols="12" md="5" class="form-section">
        <div class="form-wrapper">
          <!-- Logo mobile -->
          <v-img
              src="../../assets/icons/Gestor_Logo_.svg"
              max-width="200"
              contain
              class="mb-6 mb-sm-8 mx-auto d-md-none"
          ></v-img>

          <div class="text-center mb-6 mb-sm-4 d-none d-md-block">
            <h2 :class="[$vuetify.breakpoint.xs ? 'text-h5' : 'text-h4', $vuetify.breakpoint.smAndDown ? 'white--text' : 'primary--text']" class="font-weight-bold mb-2">
              Acesse sua conta
            </h2>
            <p :class="$vuetify.breakpoint.smAndDown ? 'white--text text--lighten-1' : 'grey--text'" class="text-body-2">
              Insira suas credenciais para autenticar
            </p>
          </div>

          <v-card flat class="login-card elevation-0">
            <v-form @submit.prevent="logar" ref="loginForm">
              <v-card-text :class="$vuetify.breakpoint.xs ? 'px-2' : 'px-0'">
                <!-- Campo Usuário -->
                <div class="mb-4 mb-sm-6">
                  <label :class="$vuetify.breakpoint.smAndDown ? 'white--text' : 'grey--text text--darken-2'" class="text-body-2 font-weight-medium mb-2 d-block">
                    Usuário
                  </label>
                  <v-text-field
                      v-model="dados.usuario"
                      name="usuario"
                      placeholder="Digite seu usuário"
                      autofocus
                      outlined
                      dense
                      hide-details="auto"
                      prepend-inner-icon="mdi-account-outline"
                      :error="!!erro"
                      class="custom-input"
                  />
                </div>

                <!-- Campo Senha -->
                <div class="mb-4 mb-sm-6">
                  <label :class="$vuetify.breakpoint.smAndDown ? 'white--text' : 'grey--text text--darken-2'" class="text-body-2 font-weight-medium mb-2 d-block">
                    Senha
                  </label>
                  <v-text-field
                      v-model="dados.senha"
                      name="senha"
                      placeholder="Digite sua senha"
                      :type="mostrarSenha ? 'text' : 'password'"
                      outlined
                      dense
                      hide-details="auto"
                      prepend-inner-icon="mdi-lock-outline"
                      :append-icon="mostrarSenha ? 'mdi-eye-off' : 'mdi-eye'"
                      @click:append="mostrarSenha = !mostrarSenha"
                      :error="!!erro"
                      class="custom-input"
                  />
                </div>

                <!-- Alerta de Erro -->
                <v-expand-transition>
                  <v-alert
                      v-if="erro"
                      type="error"
                      dense
                      text
                      dismissible
                      icon="mdi-alert-circle-outline"
                      class="mb-4"
                      @input="erro = ''"
                  >
                    {{ erro }}
                  </v-alert>
                </v-expand-transition>

                <!-- Botão Login -->
                <v-btn
                    type="submit"
                    :loading="carregando"
                    :disabled="carregando"
                    color="primary"
                    :x-large="!$vuetify.breakpoint.xs"
                    :large="$vuetify.breakpoint.xs"
                    block
                    elevation="2"
                    class="font-weight-bold text-none rounded-lg mb-4"
                >
                  <v-icon left :small="$vuetify.breakpoint.xs">mdi-login</v-icon>
                  Entrar no Sistema
                </v-btn>
              </v-card-text>
            </v-form>
          </v-card>

          <!-- Footer -->
          <div class="text-center mt-6 mt-sm-4">
            <p :class="[$vuetify.breakpoint.xs ? 'text-caption' : 'text-body-2', $vuetify.breakpoint.smAndDown ? 'white--text text--lighten-1' : 'grey--text']">
              © {{ new Date().getFullYear() }} Sistema Gestor. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import axios from "axios";

export default {
  name: "Login",

  data: () => ({
    carregando: false,
    erro: "",
    dados: {},
    mostrarSenha: false,
    featuresVisiveis: [],
    features: [
      {
        icon: 'mdi-view-dashboard-outline',
        title: 'Gestão Integrada',
        subtitle: 'Todos os setores da sua empresa em um só lugar'
      },
      {
        icon: 'mdi-chart-timeline-variant',
        title: 'Inteligência de Dados',
        subtitle: 'Relatórios e análises em tempo real'
      },
      {
        icon: 'mdi-sync',
        title: 'Processos Automatizados',
        subtitle: 'Fluxos de trabalho otimizados entre departamentos'
      },
      {
        icon: 'mdi-lightbulb-on-outline',
        title: 'Apoio à Tomada de Decisões',
        subtitle: 'Insights estratégicos e indicadores de performance'
      }
    ]
  }),

  computed: {
    ...mapState(["backendUrl", "sessaoChave"]),
  },

  methods: {
    ...mapMutations(["login"]),

    /**
     * Limpa cache do navegador para garantir atualização do front-end
     * NÃO faz reload - isso fica a cargo de quem chamar
     */
    async limparCache() {
      try {
        // 1. Limpa Service Workers
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const registration of registrations) {
            await registration.unregister();
          }
          // console.log('✅ Service Workers removidos');
        }

        // 2. Limpa Cache Storage API
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(
              cacheNames.map(cacheName => caches.delete(cacheName))
          );
          // console.log('✅ Cache Storage limpo:', cacheNames);
        }

        // 3. Limpa sessionStorage (exceto a flag de controle do reload)
        const loginReloadDone = sessionStorage.getItem('login_reload_done');
        sessionStorage.clear();
        // Restaura flag se existir
        if (loginReloadDone) {
          sessionStorage.setItem('login_reload_done', loginReloadDone);
        }
        // console.log('✅ sessionStorage limpo');

        // console.log('✅ Cache limpo com sucesso');
      } catch (error) {
        console.warn('⚠️ Erro ao limpar cache:', error);
      }
    },

    /**
     * Realiza o login do usuário
     */
    async logar() {
      // Validações
      if (!this.dados.usuario) {
        return (this.erro = "Campo de usuário é obrigatório");
      }
      if (!this.dados.senha) {
        return (this.erro = "Campo de senha é obrigatório");
      }

      this.carregando = true;
      this.erro = "";

      try {
        const res = await axios.post(`${this.backendUrl}login`, this.dados);

        this.dados = {};

        // Salva dados da sessão
        this.$store.commit("uLogar", { ...res.data });
        localStorage.setItem(this.sessaoChave, JSON.stringify(res.data));

        // Limpa o cache
        await this.limparCache();

        // Navega para home usando Vue Router (sem recarregar página)
        this.$router.push('/').catch(err => {
          // Ignora erro de navegação duplicada
          if (err.name !== 'NavigationDuplicated') {
            console.error('Erro ao navegar:', err);
          }
        });

        // Não precisa mais de carregando = false pois vai navegar
      } catch (e) {
        this.erro = this.erroFormatar(e);
        this.carregando = false;
      }
    },

    /**
     * Carrega as features em sequência com animação
     */
    carregarFeaturesSequencialmente() {
      this.features.forEach((feature, index) => {
        setTimeout(() => {
          this.featuresVisiveis.push(feature.icon);
        }, index * 150);
      });
    }
  },

  mounted() {
    // Inicia o carregamento das features em sequência
    this.carregarFeaturesSequencialmente();
  },
};
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ebf0 100%);
}

/* Seção de Informações (Lado Esquerdo - Desktop) */
.info-section {
  background: linear-gradient(135deg, #023449 0%, #034d6b 100%);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
}

.info-section::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
  animation: pulse 15s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.1); opacity: 0.3; }
}

.info-content {
  position: relative;
  z-index: 1;
  max-width: 500px;
  width: 100%;
}

/* Ajustes para o lado esquerdo em diferentes resoluções */
@media (min-width: 961px) and (max-width: 1280px) {
  .info-section {
    padding: 40px 30px;
  }

  .info-content {
    max-width: 450px;
  }
}

@media (min-width: 1281px) and (max-width: 1440px) {
  .info-section {
    padding: 50px 40px;
  }

  .info-content {
    max-width: 480px;
  }
}

.features-list {
  margin-top: 40px;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  min-height: 60px;
  position: relative;
}

.feature-content {
  display: flex;
  align-items: flex-start;
  width: 100%;
}

/* Transição para features carregando em sequência */
.feature-fade-enter-active {
  transition: all 0.6s ease-out;
}

.feature-fade-enter {
  opacity: 0;
  transform: translateX(-30px);
}

.feature-fade-leave-active {
  transition: all 0.3s ease-in;
}

.feature-fade-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* Seção do Formulário (Lado Direito) */
.form-section {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

/* Fundo escuro em dispositivos móveis */
@media (max-width: 960px) {
  .form-section {
    background: linear-gradient(135deg, #023449 0%, #034d6b 100%);
    position: relative;
    overflow: hidden;
  }

  .form-section::before {
    content: '';
    position: absolute;
    top: -30%;
    left: -30%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%);
    animation: pulse 15s ease-in-out infinite;
  }

  .form-wrapper {
    position: relative;
    z-index: 1;
  }
}

.form-wrapper {
  width: 100%;
  max-width: 450px;
  animation: fadeIn 0.6s ease-out;
}

/* Ajuste para telas menores em desktop */
@media (min-width: 961px) and (max-width: 1280px) {
  .form-wrapper {
    max-width: 380px;
  }

  .form-section {
    padding: 30px 15px;
  }
}

/* Ajuste para resoluções entre 1280px e 1440px */
@media (min-width: 1281px) and (max-width: 1440px) {
  .form-wrapper {
    max-width: 400px;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-card {
  background: transparent !important;
}

/* Inputs Customizados */
.custom-input >>> .v-input__slot {
  background: white !important;
  border-radius: 8px !important;
  min-height: 48px !important;
}

.custom-input >>> .v-text-field__slot input {
  font-size: 14px;
}

.custom-input >>> fieldset {
  border-color: #e0e0e0 !important;
  border-width: 1.5px !important;
}

.custom-input >>> .v-input__slot:hover fieldset {
  border-color: #023449 !important;
}

.custom-input.v-input--is-focused >>> fieldset {
  border-color: #023449 !important;
  border-width: 2px !important;
}

.custom-input.error--text >>> fieldset {
  border-color: #ff5252 !important;
}

/* Botão de Login */
.v-btn {
  text-transform: none !important;
  letter-spacing: 0.5px;
}

/* Responsividade */
@media (max-width: 960px) {
  .form-section {
    padding: 30px 20px;
    min-height: 100vh;
  }

  .form-wrapper {
    max-width: 400px;
  }
}

@media (max-width: 600px) {
  .form-section {
    padding: 20px 16px;
    min-height: 100vh;
  }

  .form-wrapper {
    max-width: 100%;
  }

  .custom-input >>> .v-input__slot {
    min-height: 44px !important;
  }

  .v-btn.large {
    height: 44px !important;
  }
}

@media (max-width: 380px) {
  .form-section {
    padding: 15px 12px;
  }

  .custom-input >>> .v-input__slot {
    min-height: 42px !important;
  }

  .custom-input >>> .v-text-field__slot input {
    font-size: 13px;
  }

  .v-btn.large {
    height: 42px !important;
    font-size: 14px !important;
  }
}

@media (max-height: 500px) and (orientation: landscape) {
  .form-section {
    padding: 15px 20px;
  }

  .form-wrapper {
    max-width: 450px;
  }

  .mb-6, .mb-sm-6 {
    margin-bottom: 12px !important;
  }

  .mb-8, .mb-sm-8 {
    margin-bottom: 16px !important;
  }
}

@media (min-width: 1920px) {
  .info-content {
    max-width: 600px;
  }

  .info-section {
    padding: 80px;
  }

  .form-wrapper {
    max-width: 500px;
  }
}
</style>